import type { Editor, JSONContent } from "@tiptap/react";
import { chipAttrsOf, type ChipMarkdownSpec, type ChipNodeMeta } from "./chip-extension";

// The plain-text value format: a surface whose stored string is literal text
// with chip tags in it (workflow node config — `{{path}}` inside an email
// subject, a task title, a prompt) and nothing else.
//
// This exists because markdown is *lossy* for those strings. tiptap-markdown
// would read `- ` as a bullet, `# ` as a heading and `*` as emphasis on load,
// then escape them back out on save — so opening a workflow panel and typing
// one character would rewrite an email body's leading dashes, asterisks and
// numbered lines. Text surfaces skip the markdown pipeline in both directions:
// one paragraph per line, and only the chip family's own text form (a
// ChipMarkdownSpec, reused verbatim) crosses the boundary.

/** Splits one line into text + chip inline nodes using the family's spec. */
function inlineNodes(
  line: string,
  spec: ChipMarkdownSpec | undefined,
  node: ChipNodeMeta,
): JSONContent[] {
  if (!line) return [];
  if (!spec) return [{ type: "text", text: line }];

  // Fresh regex per line: a caller-supplied global pattern would carry
  // `lastIndex` across calls.
  const pattern = new RegExp(spec.pattern.source, "g");
  const out: JSONContent[] = [];
  let cursor = 0;
  for (let match = pattern.exec(line); match; match = pattern.exec(line)) {
    if (match[0] === "") break;
    const attrs = spec.attrs(match);
    if (!attrs) continue;
    if (match.index > cursor) out.push({ type: "text", text: line.slice(cursor, match.index) });
    out.push({
      type: node.name,
      attrs: { id: attrs.id, label: attrs.label, [node.typeAttr]: attrs.refType },
    });
    cursor = match.index + match[0].length;
  }
  if (cursor === 0) return [{ type: "text", text: line }];
  if (cursor < line.length) out.push({ type: "text", text: line.slice(cursor) });
  return out;
}

/**
 * Plain text → Tiptap JSON: one paragraph per line (empty lines become empty
 * paragraphs, so blank-line spacing survives), with every {@link
 * ChipMarkdownSpec.pattern} match turned into a chip node.
 */
export function chipDocFromText(
  text: string,
  spec: ChipMarkdownSpec | undefined,
  node: ChipNodeMeta,
): JSONContent {
  return {
    type: "doc",
    content: text.split("\n").map((line) => {
      const content = inlineNodes(line, spec, node);
      return content.length > 0 ? { type: "paragraph", content } : { type: "paragraph" };
    }),
  };
}

/**
 * The inverse: the editor's text with chips written back as their tag text.
 * Blocks join with a single newline, matching {@link chipDocFromText}'s split.
 */
export function chipTextFromEditor(
  editor: Editor,
  spec: ChipMarkdownSpec | undefined,
  node: ChipNodeMeta,
): string {
  return editor.getText({
    blockSeparator: "\n",
    ...(spec
      ? {
          textSerializers: {
            [node.name]: ({ node: chip }) => spec.serialize(chipAttrsOf(chip.attrs, node.typeAttr)),
          },
        }
      : {}),
  });
}
